import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, Loader2, PiggyBank, Wallet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import { EmptyState, StatCard, tdClass, thClass } from "../components/Common";

const KAS_META = {
  resmi: { label: "Kas Resmi", saldoLabel: "Saldo Kas Resmi", accent: "blue" },
  imf: { label: "Kas IMF", saldoLabel: "Saldo Kas IMF", accent: "emerald" },
};

export default function Saldo() {
  const { hasPerm } = useAuth();
  const canResmi = hasPerm("view_saldo_resmi");
  const canImf = hasPerm("view_saldo_imf");
  const [book, setBook] = useState(canResmi ? "resmi" : "imf");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((b) => {
    setLoading(true);
    api.get(`/stats/saldo?book=${b}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(book); }, [book, load]);

  const meta = KAS_META[book];
  const tabCls = (active) =>
    `px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${
      active ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-100"
    }`;

  if (loading && !data)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="saldo-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Saldo {meta.label}</h2>
          <p className="text-xs text-slate-400">Pilih kas untuk melihat saldo dan mutasinya masing-masing</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-800 bg-slate-800 p-1" data-testid="saldo-kas-selector">
          {canResmi && (
            <button data-testid="saldo-tab-resmi" onClick={() => setBook("resmi")} className={tabCls(book === "resmi")}>
              Kas Resmi
            </button>
          )}
          {canImf && (
            <button data-testid="saldo-tab-imf" onClick={() => setBook("imf")} className={tabCls(book === "imf")}>
              Kas IMF
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard testid="saldo-awal-card" title={`Saldo Awal ${meta.label}`} value={formatRupiah(data.opening_balance)} numeric={data.opening_balance} icon={PiggyBank} accent="slate" sub="Diatur di menu Pengaturan" />
            <StatCard testid="saldo-pemasukan-card" title="Total Pemasukan" value={formatRupiah(data.total_income)} numeric={data.total_income} icon={ArrowDownRight} accent="emerald" />
            <StatCard testid="saldo-pengeluaran-card" title="Total Pengeluaran" value={formatRupiah(data.total_expense)} numeric={data.total_expense} icon={ArrowUpRight} accent="rose" />
            <StatCard testid="saldo-akhir-card" title={meta.saldoLabel} value={formatRupiah(data.closing_balance)} numeric={data.closing_balance} icon={Wallet} accent={meta.accent} sub="Saldo Awal + Pemasukan − Pengeluaran" />
          </div>

          <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-base font-bold text-slate-100">Mutasi {meta.label}</h3>
              <p className="text-xs text-slate-400">Riwayat perubahan saldo {meta.label} berdasarkan urutan tanggal transaksi</p>
            </div>
            {data.mutations.length === 0 ? (
              <div className="px-5 pb-8"><EmptyState title="Belum ada mutasi" description={`Mutasi ${meta.label} akan muncul setelah ada transaksi.`} /></div>
            ) : (
              <div className="overflow-x-auto max-h-[560px]">
                <table data-testid="table-mutasi-saldo" className="w-full border-collapse text-sm text-left">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className={thClass}>Tanggal</th>
                      <th className={thClass}>No Bukti</th>
                      <th className={thClass}>Uraian</th>
                      <th className={`${thClass} text-right`}>Pemasukan</th>
                      <th className={`${thClass} text-right`}>Pengeluaran</th>
                      <th className={`${thClass} text-right`}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50/70">
                      <td className={tdClass} colSpan={3}>
                        <span className="font-semibold text-slate-200">Saldo Awal</span>
                      </td>
                      <td className={tdClass} />
                      <td className={tdClass} />
                      <td className={`${tdClass} text-right font-num font-semibold text-slate-200`}>{formatRupiah(data.opening_balance)}</td>
                    </tr>
                    {data.mutations.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-800/40">
                        <td className={`${tdClass} whitespace-nowrap`}>{formatDateID(m.date)}</td>
                        <td className={`${tdClass} font-num text-xs`}>{m.transaction_number}</td>
                        <td className={`${tdClass} font-medium text-slate-100 max-w-[320px]`}>
                          <span className="line-clamp-1">{m.description}</span>
                        </td>
                        <td className={`${tdClass} text-right font-num text-emerald-400`}>{m.income ? formatRupiah(m.income) : "-"}</td>
                        <td className={`${tdClass} text-right font-num text-rose-400`}>{m.expense ? formatRupiah(m.expense) : "-"}</td>
                        <td className={`${tdClass} text-right font-num font-semibold ${m.balance < 0 ? "text-rose-400" : "text-blue-300"}`}>{formatRupiah(m.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
